import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PublicHeader, Footer } from "@/components/layout/PublicHeader";
import { CARDS, cardImage, heroSrc, useCards, useHero } from "@/components/brand/Angels";

const INSPIRATION = [
  { img: "/brand/days-at-sea.webp", title: "Days at sea", body: "Riviera coastlines, hidden coves and dinners on deck.", pos: "center 45%" },
  { img: "/brand/villa-weekend.webp", title: "Villa weekends", body: "Clifftop villas, sunset dinners and quiet mornings.", pos: "center 55%" },
  { img: "/brand/alpine-chalet.webp", title: "Alpine chalets", body: "Snow, firelight and long evenings in the mountains.", pos: "center 55%" },
];
const STEPS = [
  { n: "01", title: "Apply", body: "Create your profile and apply. Every application is reviewed before access is granted." },
  { n: "02", title: "Find your circle", body: "Discover proposed experiences or create your own, then find co-hosts who share the plan." },
  { n: "03", title: "Agree the details", body: "Agree each person's contribution individually. Shares can differ, and every co-host confirms their own." },
  { n: "04", title: "Create the experience", body: "Invite guests with covered costs made clear, then coordinate privately once the circle is agreed." },
];

const Hero = () => {
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, reduce ? 0 : 60]);
  const [hero] = useHero();
  const heroFocus = hero.focus;
  return (
    <section className="relative overflow-hidden border-b border-border" data-testid="home-hero">
      <div className="relative lg:absolute lg:inset-y-0 lg:right-0 lg:w-[62%]">
        <motion.img style={{ y, ...(heroFocus ? { objectPosition: `${heroFocus.x}% ${heroFocus.y}%`, scale: heroFocus.zoom, transformOrigin: `${heroFocus.x}% ${heroFocus.y}%` } : {}) }} src={heroSrc(hero)} alt="Genesis Members Club" fetchPriority="high"
          className="h-[310px] w-full object-cover object-[70%_center] sm:h-[440px] lg:absolute lg:inset-0 lg:h-[calc(100%+60px)] lg:-top-[30px] lg:object-[65%_center]" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-background to-transparent lg:inset-y-0 lg:left-0 lg:h-auto lg:w-1/2 lg:bg-gradient-to-r" aria-hidden />
      </div>
      <div className="shell relative pb-10 pt-2 lg:flex lg:min-h-[680px] lg:items-center lg:py-20">
        <div className="max-w-[560px] fade-up">
          <p className="eyebrow mb-6 hidden lg:block">Private members club</p>
          <h1 className="font-display text-[44px] leading-[0.98] sm:text-6xl lg:text-[84px] lg:leading-[0.95]" data-testid="hero-headline">
            <span className="block">Find your <span className="whitespace-nowrap">circle.</span></span>
            <span className="block"><span className="whitespace-nowrap">Create the</span> extraordinary.</span>
          </h1>
          <p className="mt-4 max-w-md text-base text-foreground/85 sm:mt-6 sm:text-lg">Meet your co-hosts. Build exceptional experiences. Agree the details. Make it yours.</p>
          <div className="mt-6 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:gap-5">
            <Link to="/apply" className="btn-primary" data-testid="hero-apply-button">Apply for membership</Link>
            <a href="#club" className="btn-outline" data-testid="hero-discover-button">Discover the club</a>
          </div>
          <div className="mt-12 hidden items-center gap-6 border-t border-foreground/20 pt-6 sm:flex">
            {["Co-host", "Plan", "Connect"].map((w, i) => (
              <span key={w} className="flex items-center gap-6">
                {i > 0 && <span className="h-px w-12 bg-foreground/25" aria-hidden />}
                <span className="eyebrow text-[10px]">{w}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Inspiration = () => {
  const [cards] = useCards();
  return (
  <section id="experiences" className="scroll-mt-24 py-20 lg:py-28" data-testid="home-inspiration">
    <div className="shell">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <h2 className="font-display text-4xl leading-[1.02] sm:text-5xl">Extraordinary<br />starts with your circle.</h2>
        <p className="max-w-sm text-foreground/85">From villa weekends to days at sea, the best plans begin with the right people.</p>
        <p className="eyebrow flex items-center gap-4 text-[10px]"><span className="h-px w-12 bg-foreground/30" />Experience inspiration</p>
      </div>
      <div className="mt-12 grid gap-5 md:grid-cols-3">
        {INSPIRATION.map((c, i) => (
          <figure key={c.title} className="surface card-hover fade-up overflow-hidden" style={{ animationDelay: `${i * 90}ms` }} data-testid={`inspiration-card-${i}`}>
            <div className="aspect-[4/3] overflow-hidden"><img {...cardImage(cards, CARDS[i])} alt="" loading="lazy" className="img-cover" data-testid={`inspiration-img-${i}`} /></div>
            <figcaption className="p-6">
              <h3 className="font-display text-2xl">{c.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{c.body}</p>
            </figcaption>
          </figure>
        ))}
      </div>
      <p className="mt-5 text-xs text-muted-foreground">Illustrative concepts. Not confirmed or bookable properties.</p>
    </div>
  </section>
  );
};

const HowItWorks = () => (
  <section id="how" className="scroll-mt-24 border-y border-border bg-card/40 py-20 lg:py-28" data-testid="home-how">
    <div className="shell">
      <p className="eyebrow mb-5">How it works</p>
      <h2 className="font-display max-w-2xl text-4xl leading-[1.02] sm:text-5xl">Four steps from idea to the extraordinary.</h2>
      <ol className="mt-14 grid gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((s) => (
          <li key={s.n} className="flex flex-col gap-4 bg-background p-7 lg:p-8" data-testid={`how-step-${s.n}`}>
            <span className="font-display text-5xl text-primary">{s.n}</span>
            <h3 className="font-display text-2xl">{s.title}</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">{s.body}</p>
          </li>
        ))}
      </ol>
      <p className="mt-6 text-sm text-muted-foreground">Planning only. Bookings and payments are arranged outside the platform.</p>
    </div>
  </section>
);

const Club = () => (
  <section id="club" className="scroll-mt-24 py-20 lg:py-28" data-testid="home-club">
    <div className="shell grid items-center gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
      <div className="relative grid grid-cols-5 gap-4">
        <img src="/brand/planning-v2.webp" alt="Members planning an experience together" loading="lazy" className="col-span-3 aspect-[3/4.4] w-full rounded-xl object-cover object-[center_40%]" />
        <img src="/brand/yacht-v2.webp" alt="Members at a yacht gathering" loading="lazy" className="col-span-2 mt-16 aspect-[3/5] w-full rounded-xl object-cover object-[30%_60%]" />
      </div>
      <div>
        <p className="eyebrow mb-5">The club</p>
        <h2 className="font-display text-4xl leading-[1.02] sm:text-5xl" data-testid="club-heading">A private circle. Extraordinary possibilities.</h2>
        <div className="mt-8 space-y-5 text-foreground/85">
          <p className="text-lg text-foreground">The most exceptional experiences begin with the right people.</p>
          <p>Welcome to Genesis Members Club, a private members club where approved adults connect, collaborate and bring extraordinary ideas to life. The possibilities begin with your imagination. From luxury yacht escapes to stunning private villas, alpine retreats and exclusive destination experiences.</p>
          <h3 className="pt-4 font-display text-2xl text-foreground">Your vision. Your circle. Your experience.</h3>
          <p>Create your own experience or discover an idea worth joining. Connect with like-minded co-hosts, shape the details together and agree contributions that work for everyone. Every co-host confirms their own share before an arrangement is made, while invited guests enjoy complete clarity about what's included and what isn't.</p>
        </div>
      </div>
    </div>
  </section>
);

const FinalCta = () => (
  <section className="relative overflow-hidden border-t border-border" data-testid="home-final-cta">
    <img src="/brand/yacht-v2.webp" alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover object-[center_35%] opacity-35" loading="lazy" />
    <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/30" aria-hidden />
    <div className="shell relative py-24 lg:py-32">
      <h2 className="font-display max-w-xl text-5xl leading-[0.98] sm:text-6xl">Your circle starts here.</h2>
      <p className="mt-6 max-w-md text-foreground/85">Apply to join a community that creates exceptional experiences together. Membership is subject to approval.</p>
      <Link to="/apply" className="btn-primary mt-9" data-testid="final-apply-button">Apply for membership <ArrowRight className="h-4 w-4" /></Link>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <PublicHeader />
      <Hero />
      <Inspiration />
      <HowItWorks />
      <Club />
      <FinalCta />
      <Footer />
    </div>
  );
}
